import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  Archive,
  ArchiveRestore,
  KeyRound,
  RotateCcwKey,
  Shield,
  ShieldOff,
} from "lucide-react";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  supportedLanguages,
  userStatuses,
} from "../../../../src/constants/enum-to-text";
import { UserPasswordStatus, UserStatus } from "../../../api";
import { analytics } from "../../../app/analytics";
import { AppModel } from "../../../app/app-model";
import { AddButton } from "../../../components/ui-kit/add-button";
import { HelperTooltip } from "../../../components/ui-kit/base/helper-tooltip";
import { Tooltip } from "../../../components/ui-kit/base/tooltip";
import { Button } from "../../../components/ui-kit/button";
import { useDialog } from "../../../components/ui-kit/dialog-provider";
import { resolveEnumLabel } from "../../../components/ui-kit/fields/utils";
import { CellOverflowList } from "../../../components/ui-kit/tables/cells/cell-overflow-list";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import { InfoTable } from "../../../components/ui-kit/tables/info-table";
import { connectedRoles } from "../../../constants/constants";
import { hasAdminAccess } from "../../../constants/permissions";
import { uniqueValueOptions } from "../../../utils/filter-presets";
import { assert, dateTimeOrNull } from "../../../utils/utils";
import { archiveConsequences, UserPassStatus } from "../admin-console";
import { AddScreeningUserDialog } from "./add-screening-user-dialog";
import { AddUserWizard } from "./add-user-wizard";
import { useDeleteUserFlow } from "./delete-user-dialog";
import { TemporaryPasswordDialog } from "./temporary-password-dialog";
import { UserEditDialog } from "./user-edit-dialog";
import { UserPermissionsDialog } from "./user-permissions-dialog";
import { UsersItem } from "./users";

export const UsersRoute = observer(function UsersRoute(props: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const admin = props.app.requireAdminShit();
  const model = admin.users;
  assert(model, "Users model not initialized");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showScreeningDialog, setShowScreeningDialog] = useState(false);
  const [permissionsFor, setPermissionsFor] = useState<UsersItem | null>(null);
  const isAdmin = hasAdminAccess(admin.api.permissions);
  const { alert, confirm } = useDialog();
  const { confirmDelete, deleteUser } = useDeleteUserFlow(admin);

  useEffect(() => {
    analytics.trackOncePerSession("Admin Users Viewed");
  }, []);

  async function resetMfa(item: UsersItem) {
    const confirmed = await confirm(
      t`This removes ${item.username}'s authenticator app, passkeys and backup codes, and signs them out everywhere. They will have to set two-factor authentication up again the next time they log in.`,
      t`Reset two-factor authentication?`,
      { confirmLabel: t`Reset` },
    );

    if (!confirmed) {
      return;
    }

    await admin.resetMfa(item.uuid);
    await alert(t`Two-factor authentication was reset for ${item.username}.`);
  }

  async function archive(item: UsersItem) {
    const confirmed = await confirm(
      resolveEnumLabel(archiveConsequences),
      t`Archive ${item.username}?`,
      { confirmLabel: t`Archive` },
    );

    if (!confirmed) {
      return;
    }

    await admin.archiveUser(item.uuid);
  }

  async function unarchive(item: UsersItem) {
    const confirmed = await confirm(
      t`${item.username} gets a password-reset email and can log in again once they follow it. The physicians, clinics and visible patients stripped on archival are not restored — set them again from this table.`,
      t`Restore ${item.username}?`,
      { confirmLabel: t`Restore` },
    );

    if (!confirmed) {
      return;
    }

    await admin.unarchiveUser(item.uuid);
  }

  return (
    <>
      <TemporaryPasswordDialog admin={admin} />

      {permissionsFor && (
        <UserPermissionsDialog
          item={permissionsFor}
          admin={admin}
          users={model}
          onClose={() => setPermissionsFor(null)}
        />
      )}

      {isAdmin && (
        <>
          {showAddDialog && (
            <AddUserWizard
              app={props.app}
              admin={admin}
              onClose={() => setShowAddDialog(false)}
              onSaved={() => {
                void admin.refreshUsers();
                // The wizard's step 2 wrote the new user's policy; the
                // Permissions dialog joins policies onto rows by user uuid.
                void admin.refreshAccessPolicies();
              }}
            />
          )}
          {showScreeningDialog && (
            <AddScreeningUserDialog
              admin={admin}
              onClose={() => setShowScreeningDialog(false)}
              onSaved={() => {
                void admin.refreshUsers();
                // POST /billing/users also creates the physician record and the
                // access policy; the Permissions dialog joins policies by uuid.
                void admin.refreshAccessPolicies();
                void admin.refreshPhysicians();
              }}
            />
          )}
        </>
      )}

      <DataTable
        persistState
        editMode="dialog"
        itemKeyField="uuid"
        filterNoun={(count) =>
          t`${plural(count, { one: "user", other: "users" })}`
        }
        filterActions={
          <AddButton
            items={[
              { label: t`Regular user`, onClick: () => setShowAddDialog(true) },
              {
                label: t`Screening user`,
                onClick: () => setShowScreeningDialog(true),
              },
            ]}
          >
            <Trans>New user</Trans>
          </AddButton>
        }
        columns={[
          {
            header: <Trans>Username</Trans>,
            attr: "username",
            type: "string",
            sort: true,
            filter: { kind: "text", group: t`Users`, placeholder: t`Search…` },
            sticky: true,
            cell: ({ item }) => <strong>{item.username}</strong>,
          },
          {
            header: <Trans>Role</Trans>,
            attr: "role",
            type: "string",
            filter: {
              kind: "multi",
              group: t`Users`,
              options: uniqueValueOptions<UsersItem>("role"),
            },
          },
          {
            header: <Trans>Email</Trans>,
            attr: "email",
            type: "string",
            width: 220,
            editable: hasAdminAccess(admin.api.permissions),
            copyable: true,
          },
          {
            header: <Trans>Status</Trans>,
            attr: "status",
            type: "enum",
            filter: {
              kind: "multi",
              group: t`Users`,
              options: new Map(Object.entries(userStatuses)),
            },
            values: userStatuses,
            cell: ({ item }) => (
              <span
                className={
                  item.status === UserStatus.active
                    ? "text-success-foreground"
                    : item.status === UserStatus.not_active
                      ? "text-danger-foreground"
                      : item.status === UserStatus.archived
                        ? "text-muted-foreground"
                        : "text-warning-foreground"
                }
              >
                {resolveEnumLabel(userStatuses[item.status])}
                {item.last_requested_reset && (
                  <HelperTooltip
                    className="ml-1"
                    content={t`Password reset requested on: ${dateTimeOrNull(item.last_requested_reset)}`}
                  />
                )}
              </span>
            ),
          },
          {
            header: <Trans>Added</Trans>,
            attr: "created_at",
            type: "datetime",
            sort: true,
            initSort: "desc",
          },
          {
            header: <Trans>Added by</Trans>,
            attr: "added_by",
            type: "enum",
            values: model.userMapping,
            filter: {
              kind: "multi",
              group: t`Users`,
              // Read userMapping lazily — it fills in after the users load.
              options: () => new Map(Object.entries(model.userMapping)),
            },
          },
          {
            header: <Trans>Last login</Trans>,
            attr: "last_login",
            type: "datetime",
          },
          {
            header: <Trans>Preferred language</Trans>,
            attr: "preferred_language",
            type: "enum",
            values: supportedLanguages,
            editable: true,
            width: 100,
          },
          {
            header: <Trans>UUID</Trans>,
            attr: "uuid",
            type: "string",
            width: 300,
            copyable: true,
            filter: {
              kind: "text",
              group: t`Users`,
              placeholder: t`Paste UUID…`,
            },
          },
          {
            header: <Trans>Connected ID</Trans>,
            cell: ({ item }) => (
              <strong>
                <Link
                  className="text-brand-primary"
                  to={`/patient/${item.connected_patient_id}`}
                >
                  {item.connected_patient_id}
                </Link>
              </strong>
            ),
          },
          {
            header: <Trans>Managed IDs</Trans>,
            cell: ({ item }) => (
              <CellOverflowList
                items={(item.visible_patients ?? []).map((p) => (
                  <strong key={p.patient_id}>
                    <Link
                      className="text-brand-primary"
                      to={`/patient/${p.patient_id}`}
                    >
                      {p.patient_id}
                    </Link>
                  </strong>
                ))}
              />
            ),
          },
          {
            header: (
              <span className="flex items-center gap-1">
                <Trans>Reset user</Trans>
                <HelperTooltip
                  content={t`Send 'forgot password' reset email.`}
                />
              </span>
            ),
            cell: ({ item }) =>
              connectedRoles.includes(item.role) && (
                <div>
                  <Button
                    variant="subtle"
                    aria-label={t`Reset user`}
                    disabled={item.status === UserStatus.archived}
                    onClick={async () => {
                      await admin.resetUser(item.uuid);
                    }}
                  >
                    <RotateCcwKey size={16} className="text-foreground" />
                  </Button>
                </div>
              ),
          },
          {
            // Not gated on role: two-factor is required for staff and optional
            // for patients, so any account can have something to clear.
            width: 140,
            header: (
              <span className="flex items-center gap-1">
                <Trans>Reset two-factor</Trans>
                <HelperTooltip
                  content={t`Clear every two-factor method on this account.`}
                />
              </span>
            ),
            cell: ({ item }) => (
              <div>
                <Button
                  variant="subtle"
                  aria-label={t`Reset two-factor`}
                  onClick={() => resetMfa(item)}
                >
                  <ShieldOff size={16} className="text-foreground" />
                </Button>
              </div>
            ),
          },
          {
            // The per-row tooltip stays: it is that user's password status, the
            // only place the value is shown. The header explains the button.
            header: (
              <span className="flex items-center gap-1">
                <Trans>Password state</Trans>
                <HelperTooltip
                  content={t`Issue a one-time temporary password, valid 2 days. Each button's tooltip shows that user's current password state.`}
                />
              </span>
            ),
            cell: ({ item }) =>
              connectedRoles.includes(item.role) && (
                <div>
                  <Tooltip
                    content={
                      item.password_status
                        ? resolveEnumLabel(UserPassStatus[item.password_status])
                        : undefined
                    }
                  >
                    <Button
                      variant="subtle"
                      aria-label={t`Temp. password`}
                      disabled={
                        item.status === UserStatus.archived ||
                        item.password_status ===
                          UserPasswordStatus.temporary_valid ||
                        item.password_status ===
                          UserPasswordStatus.temporary_error
                      }
                      onClick={async () => {
                        await admin.temporaryPassword(item.uuid);
                      }}
                    >
                      <KeyRound size={16} className="text-foreground" />
                    </Button>
                  </Tooltip>
                </div>
              ),
          },
          // Both columns are admin-only.
          ...(isAdmin
            ? [
                {
                  width: 100,
                  header: (
                    <span className="flex items-center gap-1">
                      <Trans>Archive</Trans>
                      <HelperTooltip
                        content={t`Close an account without deleting it — the login stops working, but everything the user authored keeps their name on it.`}
                      />
                    </span>
                  ),
                  cell: ({ item }: { item: UsersItem }) =>
                    item.status === UserStatus.archived ? (
                      <div>
                        <Tooltip
                          content={t`Sends a reset email. Assignments are not restored.`}
                        >
                          <Button
                            variant="subtle"
                            aria-label={t`Restore user`}
                            onClick={() => unarchive(item)}
                          >
                            <ArchiveRestore
                              size={16}
                              className="text-foreground"
                            />
                          </Button>
                        </Tooltip>
                      </div>
                    ) : (
                      <div>
                        <Button
                          variant="subtle"
                          aria-label={t`Archive user`}
                          onClick={() => archive(item)}
                        >
                          <Archive size={16} className="text-foreground" />
                        </Button>
                      </div>
                    ),
                },
                // Per the spec: a user cannot set even their own flags. The
                // `fe_*` flags apply to staff, who have no other screen to be
                // toggled from - the patient settings page only reaches a
                // patient's own connected user.
                {
                  header: (
                    <span className="flex items-center gap-1">
                      <Trans>Permissions</Trans>
                      <HelperTooltip
                        content={t`Show and set what a user may do and see: permissions, visibility scope and feature flags.`}
                      />
                    </span>
                  ),
                  cell: ({ item }: { item: UsersItem }) => (
                    <div>
                      <Button
                        variant="subtle"
                        aria-label={t`Permissions`}
                        onClick={() => setPermissionsFor(item)}
                      >
                        <Shield size={16} className="text-foreground" />
                      </Button>
                    </div>
                  ),
                },
              ]
            : []),
        ]}
        model={model}
        renderEditDialog={({ item, onClose, onSave }) => (
          <UserEditDialog
            item={item}
            admin={admin}
            onClose={onClose}
            onSave={onSave}
          />
        )}
        // The dialog already diffed the edit down to what changed, and every
        // key it can carry is an `UpdateUserAdminSchema` field, so it goes
        // through as-is. Picking keys off it would turn every untouched one
        // into an explicit `undefined`. Visible patients are absent by design:
        // they belong to the access policy, edited in the permissions dialog.
        onEdit={(editedField, item) => admin.updateUser(item.uuid, editedField)}
        confirmDelete={confirmDelete}
        onDelete={deleteUser}
      />

      <div className="flex flex-col items-start">
        <h4 className="mt-12 mb-2 font-bold">
          <Trans>User roles:</Trans>
        </h4>
        <InfoTable userClass>
          <tr>
            <td>
              <Trans>
                <strong>Level 1</strong> | <em>self-only</em>
              </Trans>{" "}
            </td>
            <td>
              <strong>patient-ppg-app</strong>
            </td>
            <td>
              <Trans>
                Only create PPG data and only sees its
                <em>connected patient ID</em>. This is typically user for app
                that has patient at home.
              </Trans>
            </td>
          </tr>
          <tr>
            <td>
              <Trans>
                <strong>Level 1</strong> | <em>managed</em>
              </Trans>{" "}
            </td>
            <td>
              <strong>physician-ppg-app</strong>
            </td>
            <td>
              <Trans>
                Only create PPG data and can see its
                <em>managed patient IDs</em>. This can be app that some
                physician might have at their office so they can measure their
                patients.
              </Trans>
            </td>
          </tr>
          <tr>
            <td>
              <Trans>
                <strong>Level 1</strong> | <em>full</em>
              </Trans>{" "}
            </td>
            <td>
              <strong>study-ppg-app</strong>
            </td>
            <td>
              <Trans>
                Only create PPG data and can see <em>all patient IDs</em>. This
                is SmartCare app at Premedix Office, so we can measure any
                patient.
              </Trans>
            </td>
          </tr>
          <tr>
            <td>
              <Trans>
                <strong>Level 2</strong> | <em>self-only</em>
              </Trans>{" "}
            </td>
            <td>
              <strong>patient</strong>
            </td>
            <td>
              <Trans>
                Limited read/write for any data, can update data they created.
                Only sees its <em>connected patient ID</em>. This is user for
                Seerlinq phone app for submitting basic patient and medial data.
              </Trans>
            </td>
          </tr>
          <tr>
            <td>
              <Trans>
                <strong>Level 2</strong> | <em>managed</em>
              </Trans>{" "}
            </td>
            <td>
              <strong>physician</strong>
            </td>
            <td>
              <Trans>
                Limited read/write for any data, can update data they created.
                It can see their <em>managed patient IDs</em>. This is user for
                any physician that monitors their patients.
              </Trans>
            </td>
          </tr>
          <tr>
            <td>
              <Trans>
                <strong>Level 3</strong> | <em>managed</em>
              </Trans>{" "}
            </td>
            <td>
              <strong>study-physician</strong>
            </td>
            <td>
              <Trans>
                Full read/write for any data, can update data they created. It
                can see their <em>managed patient IDs</em>. This is our
                related-physician (think Amitai) that can see all and any data,
                run algorithms, but still sees only their patients.
              </Trans>
            </td>
          </tr>
          <tr>
            <td>
              <Trans>
                <strong>Level 3</strong> | <em>full</em>
              </Trans>{" "}
            </td>
            <td>
              <strong>seerlinq-user</strong>
            </td>
            <td>
              <Trans>
                Full read/write for any data, can update data they created. It
                can see
                <em>all patient IDs</em>. This is for internal Seerlinq people
                for full access to the DB.
              </Trans>
            </td>
          </tr>
          <tr>
            <td>
              <Trans>
                <strong>Level 4</strong> | <em>full</em>
              </Trans>{" "}
            </td>
            <td>
              <strong>admin</strong>
            </td>
            <td>
              <Trans>
                God of the API and DB. Can delete any data. Only admin can
                create new users.
              </Trans>
            </td>
          </tr>
        </InfoTable>
      </div>
      <br className="h-5" />
    </>
  );
});
